// FlexControl.tsx
import React from 'react';
import ControlRenderer, { Control } from '../ControlRenderer';
import { get, isEmpty, removeEmptyProperties, set } from '../utils/Utils';
import { FormModeType } from '../../FormDataProvider';
import EditIcon from '@mui/icons-material/Edit';
import AddControlIcon from '@mui/icons-material/AddCircleOutline';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutline';
import MenuIcon from '@mui/icons-material/ListAlt';
import { IconButton, Menu, MenuItem, Typography } from '@mui/material';
import ControlEditor, { getDefaultControl } from '../ControlEditor';
import { useValidation } from '@/components/ValidationProvider';
import { getPropertyRule, Rule } from '../utils/Validation';
import RuleEditor, { getDefaultRule } from '../RuleEditor';

interface FlexControlProps {
  control: Control;
  data: any;
  onDataChange: (key: string, childName: string, newData: any) => void;
  mode: FormModeType;
  onParentMenuClick?: (event: React.MouseEvent<HTMLElement>) => void; // called when a parent menu is clicked
}

/**
 * Represents a flexbox control with properties to manage layout using CSS Flexbox model.
 * Extends the base Control interface to allow flexible and responsive design patterns.
 */
export interface FlexControl extends Control {
  /**
   * Defines the direction of the flex items, correlating to CSS 'flex-direction'.
   * Possible values include 'row', 'column', 'row-reverse', or 'column-reverse'. (Optional)
   */
  flexDirection?: React.CSSProperties['flexDirection'];

  /**
   * Determines whether the flex items should wrap onto multiple lines,
   * similar to CSS 'flex-wrap' property. Options are 'nowrap', 'wrap', 'wrap-reverse'. (Optional)
   */
  flexWrap?: React.CSSProperties['flexWrap'];

  /**
   * Aligns the flex items along the main axis using CSS 'justify-content'.
   * Options include 'flex-start', 'center', 'flex-end', 'space-between', 'space-around', 'space-evenly'. (Optional)
   */
  justifyContent?: React.CSSProperties['justifyContent'];

  /**
   * Aligns flex items along the cross axis, akin to CSS 'align-items'.
   * Acceptable values are 'stretch', 'center', 'flex-start', 'flex-end', 'baseline'. (Optional)
   */
  alignItems?: React.CSSProperties['alignItems'];

  /**
   * Aligns flex lines within the container, using CSS 'align-content'.
   * Acceptable values are 'stretch', 'center', 'flex-start', 'flex-end', 'space-between', 'space-around'. (Optional)
   */
  alignContent?: React.CSSProperties['alignContent'];

  /**
   * Specifies the gap between flex items, reflecting the CSS 'gap' property.
   * The value is numeric, representing pixels or other CSS-supported units. (Optional)
   */
  gap?: number;

  /**
   * Specifies the width of columns within the flex layout.
   * Helps define the layout structure with a flexible or fixed column width. (Optional)
   */
  colWidth?: number;

  /**
   * Decides the number of columns in the flex container, useful for managing
   * grid-like layouts with a flexible column count. (Optional)
   */
  colNumber?: number;

  /**
   * If true, the flex container will be hidden when it contains no items,
   * aiding in a clean, clutter-free interface. (Optional)
   */
  hideWhenEmpty?: boolean;
}

const FlexControl: React.FC<FlexControlProps> = ({ control, data, onDataChange, mode, onParentMenuClick }) => {
  const {
    flexDirection = 'row',
    flexWrap = 'nowrap',
    justifyContent = 'flex-start',
    alignItems = 'stretch',
    alignContent = 'stretch',
    gap = 0,
    colWidth = 0,
    colNumber = 0,
    hideWhenEmpty = false
  } = control as FlexControl;

  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuAnchorElAdd, setMenuAnchorElAdd] = React.useState<null | HTMLElement>(null);
  const [currentControl, setCurrentControl] = React.useState<Control | null>(null);
  const [currentRule, setCurrentRule] = React.useState<Rule | null>(null);
  const [editControl, setEditControl] = React.useState<Control | null>(null);
  const [editRule, setEditRule] = React.useState<Rule | null>(null);
  const { rule, validateData } = useValidation();

  const handleSubmenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorElAdd(event.currentTarget);
  };

  const handleSubmenuClose = () => {
    setMenuAnchorElAdd(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, control: Control | null) => {
    setCurrentControl(control);
    const currentRule = control?.dataKey && getPropertyRule(rule, control.dataKey);
    if (currentRule) {
      setCurrentRule(currentRule);
    }
    else {
      setCurrentRule(null);
    }
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setCurrentControl(null);
    setCurrentRule(null);
  };

  var gridContext: any;
  if (control.dataKey) {
    gridContext = get(data, control.dataKey, undefined);
    if (!gridContext) {
      gridContext = control.dataContextKey === 'FORMDATA' ? [] : {};
      set(data, control.dataKey, gridContext);
      console.log(`creating a missing datacontext ${control.dataKey}`);
    }
  } else {
    gridContext = data;
  }

  if (hideWhenEmpty && isEmpty(gridContext)) {
    return;
  }

  const handleEditControl = (controlToEdit: Control) => {
    setEditControl(controlToEdit);
  };

  const handleEditRule = (ruleToEdit: Rule) => {
    setEditRule(ruleToEdit);
  };

  const handleEditRuleComplete = () => {
    if (editRule) {
      // need to remove empty props to validate
      removeEmptyProperties(editRule);
    }
    setEditRule(null);
    validateData();
  };

  const handleAddRule = (control: Control) => {
    if (rule.properties && control.dataKey) {
      var newRule = getDefaultRule(control.type);
      if (newRule) {
        rule.properties[control.dataKey] = newRule;
        setEditRule(newRule);
      }
    }
  };

  const handleDeleteRule = (control: Control) => {
    if (rule.properties && control.dataKey) {
      delete rule.properties[control.dataKey];
    }
    setEditRule(null);
    validateData();
  };

  const handleAddControl = (type: string) => {
    var newControl = getDefaultControl(type);
    if (currentControl) {
      // adding after the specified control
      let index = control.controls?.findIndex(obj => obj === currentControl);
      if (index && index !== -1) {
        control.controls?.splice(index + 1, 0, newControl);
      }
      else {
        control.controls?.push(newControl);
      }
      currentControl.controls?.push(newControl);
    } else {
      control.controls?.push(newControl);
    }

    setEditControl(newControl);
    setMenuAnchorEl(null);
    setMenuAnchorElAdd(null);
  };

  const handleRemoveControl = (controlToRemove: Control) => {
    // Remove the control from the grid's controls
    const updatedControls = control.controls?.filter((c: Control) => c !== controlToRemove);

    // Update the grid control's controls
    // Assuming you have a setControls function or similar to update state
    set(control, 'controls', updatedControls);
  };

  const renderFlexItems = () => {
    const items: React.ReactNode[] = [];
    let index = 0;

    if (!control.controls || control.controls.length == 0) {
      if (mode === 'design') {
        return (
          <>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              display: 'flex'
            }}>
              <IconButton
                style={{
                  padding: 0,
                  color: 'gray',
                  zIndex: 1000
                }}
                onClick={(event) => handleMenuOpen(event, null)}
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            </div>
          </>
        )
      }
      return;
    }

    while (index < control.controls.length) {
      const childControl = control.controls[index];
      const controlSpan = childControl?.gridPosition?.controlSpan || 1;

      const applyAlignmentStyles = (control: Control, parentFlexDirection: string) => {
        const colSpan = control.gridPosition?.colSpan || 1;

        const childStyle: React.CSSProperties = {};

        if (mode === 'design') {
          childStyle.border = '1px solid gray';
          childStyle.position = 'relative';
        }

        if (parentFlexDirection === 'row' || parentFlexDirection === 'row-reverse') {
          if (colWidth > 0) {
            childStyle.flexBasis = `${colWidth * colSpan + gap * (colSpan - 1)}px`;
          } else if (colNumber > 0) {
            childStyle.flexBasis = `calc(100% / ${colNumber} - ${gap}px)`;
          }

          // For row direction, horizontalAlignment affects alignSelf (main axis), verticalAlignment affects justifySelf (cross axis)
          switch (control.horizontalAlignment) {
            case 'left':
              childStyle.justifySelf = 'flex-start';
              break;
            case 'center':
              childStyle.justifySelf = 'center';
              break;
            case 'right':
              childStyle.justifySelf = 'flex-end';
              break;
            case 'stretch':
              childStyle.justifySelf = 'stretch';
              break;
          }
          switch (control.verticalAlignment) {
            case 'top':
              childStyle.alignSelf = 'flex-start';
              break;
            case 'middle':
              childStyle.alignSelf = 'center';
              break;
            case 'bottom':
              childStyle.alignSelf = 'flex-end';
              break;
            case 'stretch':
              childStyle.alignSelf = 'stretch';
              childStyle.width = '100%';
              break;
          }
        } else {
          if (colWidth > 0) {
            childStyle.width = `${colWidth + gap}px`;
          }

          // For column direction, verticalAlignment affects alignSelf (main axis), horizontalAlignment affects justifySelf (cross axis)
          switch (control.verticalAlignment) {
            case 'top':
              childStyle.justifySelf = 'flex-start';
              break;
            case 'middle':
              childStyle.justifySelf = 'center';
              break;
            case 'bottom':
              childStyle.justifySelf = 'flex-end';
              break;
            case 'stretch':
              childStyle.justifySelf = 'stretch';
              childStyle.width = '100%';
              break;
          }
          switch (control.horizontalAlignment) {
            case 'left':
              childStyle.alignSelf = 'flex-start';
              break;
            case 'center':
              childStyle.alignSelf = 'center';
              break;
            case 'right':
              childStyle.alignSelf = 'flex-end';
              break;
            case 'stretch':
              childStyle.alignSelf = 'stretch';
              break;
          }
        }

        return childStyle;
      };

      if (controlSpan === 1) {
        // Single control, apply alignment styles directly
        const childKey = `flex-item-${index}`;
        const childStyle = applyAlignmentStyles(childControl, flexDirection);

        if (childControl === currentControl) {
          childStyle.border = '1px solid red';
        }

        items.push(
          <div key={childKey} style={childStyle}>
            <ControlRenderer
              key={childControl.name}
              control={childControl}
              data={gridContext}
              onDataChange={(newData: any) =>
                onDataChange(control.name, childControl.name, newData)
              }
              mode={mode}
              onParentMenuClick={(event) => handleMenuOpen(event, childControl)}
            />
            {mode === 'design' && (
              <>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  display: 'flex'
                }}>
                  <IconButton
                    style={{
                      padding: 0,
                      color: childControl.dataKey && getPropertyRule(rule, childControl.dataKey) ? 'purple' : 'gray',
                      zIndex: 1000
                    }}
                    onClick={(event) => handleMenuOpen(event, childControl)}
                  >
                    <MenuIcon fontSize="small" />
                  </IconButton>
                </div>
              </>
            )
            }
          </div>
        );
      } else {
        // Grouped controls
        const groupedControls = control.controls.slice(index, index + controlSpan);
        const groupKey = `flex-group-${index}`;
        const groupStyle: React.CSSProperties = {
          display: 'flex',
          flexDirection,
          gap: `${gap}px`,
        };

        const colSpan = groupedControls[0].gridPosition?.colSpan || 1;

        if (flexDirection === 'row' || flexDirection === 'row-reverse') {
          if (colWidth > 0) {
            groupStyle.flexBasis = `${colWidth * colSpan + gap * (colSpan - 1)}px`;
          } else if (colNumber > 0) {
            groupStyle.flexBasis = `calc(100% / ${colNumber} - ${gap}px)`;
          }
        }
        else {
          if (colWidth > 0) {
            groupStyle.width = `${colWidth + gap}px`;
          }
        }

        items.push(
          <div key={groupKey} style={groupStyle}>
            {groupedControls.map((groupedControl, groupIndex) => {
              const groupedChildKey = `${groupKey}-${groupIndex}`;
              const groupedChildStyle = applyAlignmentStyles(groupedControl, flexDirection);

              return (
                <div key={groupedChildKey} style={groupedChildStyle}>
                  <ControlRenderer
                    key={groupedControl.name}
                    control={groupedControl}
                    data={gridContext}
                    onDataChange={(newData: any) =>
                      onDataChange(control.name, groupedControl.name, newData)
                    }
                    mode={mode}
                    onParentMenuClick={(event) => handleMenuOpen(event, groupedControl)}
                  />
                  {mode === 'design' && (
                    <>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        display: 'flex'
                      }}>
                        <IconButton
                          style={{
                            padding: 0,
                            color: groupedControl.dataKey && getPropertyRule(rule, groupedControl.dataKey) ? 'purple' : 'gray',
                          }}
                          onClick={(event) => handleMenuOpen(event, groupedControl)}
                        >
                          <MenuIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </>
                  )
                  }
                </div>
              );
            })}
          </div>
        );
      }

      index += controlSpan;
    }

    return items;
  };

  const flexStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection,
    flexWrap,
    justifyContent,
    alignItems,
    alignContent,
    gap: `${gap}px`,
  };

  if (control.horizontalAlignment === 'stretch') {
    flexStyle.width = '100%';
  }

  return (
    <div style={flexStyle}>
      {renderFlexItems()}
      {menuAnchorEl && (<Menu
        id="control-menu"
        disableScrollLock
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {onParentMenuClick &&
          <MenuItem onClick={(event) => { onParentMenuClick(event); }}>
            <MenuIcon className="eform-menu-icon" />
            <Typography variant="inherit">Parent Control</Typography>
          </MenuItem>
        }
        <MenuItem onClick={handleSubmenuOpen}>
          <AddControlIcon className="eform-menu-icon" style={{ color: "green" }} />
          <Typography variant="inherit">Add Item to flex Control</Typography>
        </MenuItem>
        <Menu
          id="addmenu"
          anchorEl={menuAnchorElAdd}
          open={Boolean(menuAnchorElAdd)}
          onClose={handleSubmenuClose}
        >
          <MenuItem onClick={() => { handleAddControl("label") }}>
            <Typography variant="inherit">Label</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleAddControl("text") }}>
            <Typography variant="inherit">Text</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleAddControl("textarea") }}>
            <Typography variant="inherit">TextArea</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleAddControl("select") }}>
            <Typography variant="inherit">Dropdown</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleAddControl("date") }}>
            <Typography variant="inherit">DatePicker</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleAddControl("check") }}>
            <Typography variant="inherit">Checkbox</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleAddControl("button") }}>
            <Typography variant="inherit">Button</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleAddControl("grid") }}>
            <Typography variant="inherit">Grid</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleAddControl("flex") }}>
            <Typography variant="inherit">FlexGrid</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleAddControl("datagrid") }}>
            <Typography variant="inherit">DataGrid</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleAddControl("tab") }}>
            <Typography variant="inherit">Tab Control</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleAddControl("upload") }}>
            <Typography variant="inherit">Upload Control</Typography>
          </MenuItem>
        </Menu>
        {currentControl &&
          <>
            <MenuItem onClick={() => { handleEditControl(currentControl); handleMenuClose(); }}>
              <EditIcon className="eform-menu-icon" style={{ color: "green" }} />
              <Typography variant="inherit">{`Edit ${currentControl.type} Properties`}</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleRemoveControl(currentControl); handleMenuClose(); }}>
              <RemoveIcon className="eform-menu-icon" style={{ color: "red" }} />
              <Typography variant="inherit">{`Remove Control (${currentControl.type})`}</Typography>
            </MenuItem>
          </>
        }
        {currentRule && rule?.properties && currentControl?.dataKey && <>
          <MenuItem onClick={() => { handleEditRule(currentRule); handleMenuClose(); }}>
            <EditIcon className="eform-menu-icon" style={{ color: "purple" }} />
            <Typography variant="inherit">Edit Validation</Typography>
          </MenuItem>
          <MenuItem onClick={() => { handleDeleteRule(currentControl); handleMenuClose(); }}>
            <RemoveIcon className="eform-menu-icon" style={{ color: "purple" }} />
            <Typography variant="inherit">Delete Validation</Typography>
          </MenuItem>
        </>}
        {!currentRule && rule?.properties && currentControl?.dataKey && <>
          <MenuItem onClick={() => { handleAddRule(currentControl); handleMenuClose(); }}>
            <AddControlIcon className="eform-menu-icon" style={{ color: "purple" }} />
            <Typography variant="inherit">Add Validation</Typography>
          </MenuItem>
        </>}

        {/* Add more MenuItems as needed */}
      </Menu>)
      }
      {
        editControl &&
        <ControlEditor
          controlToEdit={editControl}
          data={gridContext}
          dataKey={control.dataKey}
          onComplete={() => { setEditControl(null) }}
          onError={() => setEditControl(null)} />
      }
      {editRule &&
        <RuleEditor
          ruleToEdit={editRule}
          data={gridContext}
          dataKey={control.dataKey}
          onComplete={handleEditRuleComplete}
          onError={() => setEditRule(null)} />}
    </div >
  );
};

export default FlexControl;